import { ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

export type CompanyStatus = 'operating' | 'raising' | 'exited_ma' | 'exited_ipo' | 'dissolved'

export type BatchOption = {
  id: number
  code: string
  label: string
}

export type CountryOption = {
  value: string
  label: string
}

export type IndustryOption = {
  id: number
  name: string
  emoji: string | null
}

export type CompanyFormData = {
  name: string
  legal_name: string
  oneliner_en: string
  oneliner_es: string
  description_en: string
  description_es: string
  webpage: string
  linkedin: string
  country: string
  logo: File | null
  remove_logo: boolean
  industry_ids: number[]
  founded_year: string | number
  status: CompanyStatus
  batch_id: string | number | null

  female_founder: boolean
}

type CompanyFormFieldsProps<T extends CompanyFormData> = {
  data: T
  errors: Partial<Record<keyof T, string>>
  setData: <K extends keyof T>(key: K, value: T[K]) => void
  batches: BatchOption[]
  countries: CountryOption[]
  industries: IndustryOption[]
  existingLogoUrl?: string | null
  children?: ReactNode
}

export function CompanyFormFields<T extends CompanyFormData>({
  data,
  errors,
  setData,
  batches,
  countries,
  industries,
  existingLogoUrl,
}: CompanyFormFieldsProps<T>) {
  const toggleIndustry = (id: number) => {
    const current = data.industry_ids
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id]
    setData('industry_ids' as keyof T, next as T[keyof T])
  }
  const pickedPreview = data.logo ? URL.createObjectURL(data.logo) : null
  const previewUrl = pickedPreview ?? (data.remove_logo ? null : existingLogoUrl ?? null)
  const err = (key: keyof T) => errors[key]

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="name">Company name</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData('name' as keyof T, e.target.value as T[keyof T])}
              autoFocus
            />
            {err('name' as keyof T) && (
              <p className="text-xs text-destructive">{err('name' as keyof T)}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="legal_name">Legal name</Label>
            <Input
              id="legal_name"
              value={data.legal_name}
              onChange={(e) => setData('legal_name' as keyof T, e.target.value as T[keyof T])}
            />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label>Industries</Label>
            {industries.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No industries yet — create some in Industries first.
              </p>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <span className={data.industry_ids.length === 0 ? 'text-muted-foreground' : ''}>
                    {data.industry_ids.length === 0
                      ? 'Select industries…'
                      : industries
                          .filter((i) => data.industry_ids.includes(i.id))
                          .map((i) => [i.emoji, i.name].filter(Boolean).join(' '))
                          .join(', ')}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="max-h-72 overflow-auto"
                  style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
                >
                  {industries.map((i) => (
                    <DropdownMenuCheckboxItem
                      key={i.id}
                      checked={data.industry_ids.includes(i.id)}
                      onCheckedChange={() => toggleIndustry(i.id)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {i.emoji && <span className="mr-1.5">{i.emoji}</span>}
                      {i.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {data.industry_ids.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() =>
                          setData('industry_ids' as keyof T, [] as unknown as T[keyof T])
                        }
                        className="text-muted-foreground"
                      >
                        Clear
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="oneliner_en">
              Oneliner <span className="text-muted-foreground">(English)</span>
            </Label>
            <Input
              id="oneliner_en"
              placeholder="What they do, in one sentence."
              value={data.oneliner_en}
              onChange={(e) => setData('oneliner_en' as keyof T, e.target.value as T[keyof T])}
            />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="oneliner_es">
              Oneliner <span className="text-muted-foreground">(Español)</span>
            </Label>
            <Input
              id="oneliner_es"
              placeholder="Lo que hacen, en una frase."
              value={data.oneliner_es}
              onChange={(e) => setData('oneliner_es' as keyof T, e.target.value as T[keyof T])}
            />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="description_en">
              Description <span className="text-muted-foreground">(English)</span>
            </Label>
            <Textarea
              id="description_en"
              rows={4}
              value={data.description_en}
              onChange={(e) =>
                setData('description_en' as keyof T, e.target.value as T[keyof T])
              }
            />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="description_es">
              Description <span className="text-muted-foreground">(Español)</span>
            </Label>
            <Textarea
              id="description_es"
              rows={4}
              value={data.description_es}
              onChange={(e) =>
                setData('description_es' as keyof T, e.target.value as T[keyof T])
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Classification</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="status">Status</Label>
            <Select
              id="status"
              value={data.status}
              onChange={(e) => setData('status' as keyof T, e.target.value as T[keyof T])}
            >
              <option value="operating">Operating</option>
              <option value="raising">Raising</option>
              <option value="exited_ma">Exited (M&amp;A)</option>
              <option value="exited_ipo">Exited (IPO)</option>
              <option value="dissolved">Dissolved</option>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="batch_id">Batch</Label>
            <Select
              id="batch_id"
              value={data.batch_id ?? ''}
              onChange={(e) =>
                setData('batch_id' as keyof T, (e.target.value || null) as T[keyof T])
              }
            >
              <option value="">—</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="country">Country</Label>
            <Select
              id="country"
              value={data.country}
              onChange={(e) => setData('country' as keyof T, e.target.value as T[keyof T])}
            >
              <option value="">—</option>
              {countries.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="founded_year">Founded year</Label>
            <Input
              id="founded_year"
              type="number"
              inputMode="numeric"
              min={1900}
              max={2100}
              value={data.founded_year}
              onChange={(e) =>
                setData('founded_year' as keyof T, e.target.value as T[keyof T])
              }
            />
          </div>

          <label className="flex items-center gap-3 sm:col-span-2">
            <Checkbox
              checked={data.female_founder}
              onChange={(e) =>
                setData('female_founder' as keyof T, e.target.checked as T[keyof T])
              }
            />
            <span className="text-sm">At least one female founder</span>
          </label>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Links</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="webpage">Website</Label>
            <Input
              id="webpage"
              type="url"
              placeholder="https://"
              value={data.webpage}
              onChange={(e) => setData('webpage' as keyof T, e.target.value as T[keyof T])}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="linkedin">LinkedIn</Label>
            <Input
              id="linkedin"
              type="url"
              placeholder="https://linkedin.com/company/…"
              value={data.linkedin}
              onChange={(e) => setData('linkedin' as keyof T, e.target.value as T[keyof T])}
            />
          </div>

        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Logo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Logo preview"
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Input
                id="logo"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null
                  setData('logo' as keyof T, file as T[keyof T])
                  if (file) setData('remove_logo' as keyof T, false as T[keyof T])
                }}
              />
              {existingLogoUrl && !data.logo && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={data.remove_logo}
                    onChange={(e) =>
                      setData('remove_logo' as keyof T, e.target.checked as T[keyof T])
                    }
                  />
                  Remove current logo
                </label>
              )}
            </div>
          </div>
          {err('logo' as keyof T) && (
            <p className="text-xs text-destructive">{err('logo' as keyof T)}</p>
          )}
        </CardContent>
      </Card>
    </>
  )
}
